import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertTriangle, ExternalLink, Loader2, Printer, RefreshCw, RotateCcw, Truck } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ConfirmSheet } from '../../components/ConfirmSheet';
import { ResultCard } from '../../components/ResultCard';
import { PackingRelabel } from './PackingRelabel';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import { courierWaitRequested } from '../../../../features/operatorPacking/operatorPackingActions';
import {
  printPackingLabels,
  sendToCourier,
  submitSendToCourier,
} from '../../../../features/operatorPacking/operatorPackingThunks';
import {
  COURIER_SEND_VIEW,
  RELABEL_VIEW,
  canSendPacking,
  courierSendSheet,
  labelsLeftOutText,
  leavesWithoutLabel,
  packingLabelsView,
  packingOrderName,
  printLabelsText,
  printableLabels,
  selectPackingCourierSend,
  selectPackingLabels,
  selectPackingLabelsPdf,
  selectPackingRelabel,
} from '../../../../features/operatorPacking/operatorPackingUtils';

const LINK_CLASS =
  'flex min-h-[48px] items-center justify-between gap-3 rounded-xl border-2 border-ninja-green px-4 text-base font-semibold text-ninja-green dark:text-white';

/**
 * Sending the packed order to its courier, while it has no shipment there:
 * «Enviar al courier» asks first — it cannot be taken back from the phone —,
 * then the block waits for the labels, says why the order cannot go or why
 * the attempt failed, and offers it again when that helps.
 *
 * @param {object} props
 * @param {object} props.order
 * @param {object} props.view - `courierSendView`.
 */
const CourierSend = ({ order, view }) => {
  const dispatch = useDispatch();
  const locked = useSelector((state) => !canSendPacking(state.operatorPacking));
  const sending = useSelector(selectIsRunning(sendToCourier, order.id));
  const [asking, setAsking] = useState(false);
  const sheet = courierSendSheet(order);

  // Closed whatever the answer: the band says it, and the block what follows.
  const confirm = async () => {
    await dispatch(submitSendToCourier({ orderId: order.id }));
    setAsking(false);
  };

  return (
    <div data-testid="packing-courier" data-status={view.status} className="space-y-2">
      {view.status === COURIER_SEND_VIEW.WAITING && (
        <p className="flex items-center gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
          <Loader2 size={18} aria-hidden="true" className="shrink-0 animate-spin text-ninja-green" />
          {view.text}
        </p>
      )}
      {view.status !== COURIER_SEND_VIEW.WAITING && view.text && (
        <p className="flex items-start gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
          <AlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span data-testid="packing-courier-text">{view.text}</span>
        </p>
      )}

      {view.status === COURIER_SEND_VIEW.OFFER && (
        <PendingButton
          pendingOn={sendToCourier}
          pendingKey={order.id}
          variant="outline"
          size="lg"
          fullWidth
          icon={Truck}
          disabled={locked}
          loadingLabel="Enviando…"
          onClick={() => setAsking(true)}
          data-testid="packing-courier-send"
        >
          Enviar al courier
        </PendingButton>
      )}
      {view.status === COURIER_SEND_VIEW.FAILED && view.retryable && (
        <PendingButton
          pendingOn={sendToCourier}
          pendingKey={order.id}
          variant="outline"
          size="lg"
          fullWidth
          icon={RotateCcw}
          disabled={locked}
          loadingLabel="Enviando…"
          onClick={() => setAsking(true)}
          data-testid="packing-courier-retry"
        >
          Reintentar el envío
        </PendingButton>
      )}
      {view.askSupervisor && <p className="text-sm text-text-secondary">Avísale a tu supervisor.</p>}
      {view.status === COURIER_SEND_VIEW.TIMEOUT && (
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          icon={RefreshCw}
          onClick={() => dispatch(courierWaitRequested(order.id))}
          data-testid="packing-courier-recheck"
        >
          Revisar de nuevo
        </Button>
      )}

      <ConfirmSheet
        isOpen={asking}
        title={sheet.title}
        message={sheet.message}
        confirmLabel={sheet.confirmLabel}
        busy={sending}
        busyLabel="Enviando…"
        onConfirm={confirm}
        onClose={() => setAsking(false)}
        testId="packing-courier-sheet"
        confirmTestId="packing-courier-confirm"
      >
        {sheet.warning && (
          <p
            data-testid="packing-courier-unweighed"
            className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300"
          >
            <AlertTriangle size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
            {sheet.warning}
          </p>
        )}
      </ConfirmSheet>
    </div>
  );
};

/**
 * The order on screen, packed: its labels — a link per item, or why there is
 * none — once the server sends them. With two or more, one tap merges them
 * into a PDF to print at once; the links stay, for a label the PDF left out
 * or a browser that keeps it from opening. Without a shipment, and once the
 * backend sends from the floor, the order goes to its courier from here; once
 * the floor changed the items of a labeled one, its shipment is created again
 * (`PackingRelabel`) and the old label is not offered. The way on («Siguiente
 * orden») is the screen's primary, in its action bar. While the order still
 * has no label and the floor can ask for it, the card warns, and the primary
 * asks before it leaves the order so.
 *
 * @param {object} props
 * @param {object} props.order
 */
export const PackingResult = ({ order }) => {
  const dispatch = useDispatch();
  const labels = useSelector(selectPackingLabels);
  const pdf = useSelector(selectPackingLabelsPdf);
  const relabel = useSelector(selectPackingRelabel);
  const view = packingLabelsView(labels);
  const courier = useSelector(selectPackingCourierSend);
  const { urls } = printableLabels(labels);
  const leftOut = pdf && labelsLeftOutText(pdf);
  // While the labels are on their way, what they show is a step: «no tiene etiqueta», or some still missing.
  const waiting = courier?.status === COURIER_SEND_VIEW.WAITING || relabel?.status === RELABEL_VIEW.WAITING;
  // A stale label has nothing to offer: the relabel block says what follows.
  const showLabels = view && !waiting && !(relabel && view.status === 'stale');
  // Made again with its label, or on its way, the shipment is fine; anything else is to act on.
  const relabelWarns =
    Boolean(relabel) &&
    relabel.status !== RELABEL_VIEW.WAITING &&
    (relabel.status !== RELABEL_VIEW.DONE || Boolean(relabel.labelFailed));
  // Packed is not done while the floor still owes it its label: Despacho does not take it without one.
  const tone = (showLabels && view.tone === 'warning') || relabelWarns || leavesWithoutLabel(courier) ? 'warning' : 'success';

  return (
    <ResultCard title={`${packingOrderName(order)} empacada`} tone={tone} testId="packing-result">
      {showLabels && (
        <div data-testid="packing-label-status" data-status={view.status} className="space-y-2">
          {view.text && <p className="text-sm font-semibold text-ninja-dark dark:text-white">{view.text}</p>}
          {urls.length >= 2 && (
            <PendingButton
              pendingOn={printPackingLabels}
              pendingKey={order.id}
              variant="outline"
              size="lg"
              fullWidth
              icon={Printer}
              loadingLabel="Armando el PDF…"
              onClick={() => dispatch(printPackingLabels({ orderId: order.id }))}
              data-testid="packing-print-labels"
              data-count={urls.length}
            >
              {printLabelsText(labels)}
            </PendingButton>
          )}
          {pdf && (
            // A real tap: no popup blocker stops it.
            <a
              href={pdf.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="packing-labels-pdf"
              data-opened={String(pdf.opened)}
              className={LINK_CLASS}
            >
              Abrir el PDF de etiquetas
              <ExternalLink size={18} aria-hidden="true" className="shrink-0" />
            </a>
          )}
          {leftOut && (
            <p data-testid="packing-labels-print-note" className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {leftOut}
            </p>
          )}
          {view.links.map((link) => (
            <a
              key={link.key}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="packing-label"
              data-item-index={link.itemIndex ?? undefined}
              className={LINK_CLASS}
            >
              {link.text}
              <ExternalLink size={18} aria-hidden="true" className="shrink-0" />
            </a>
          ))}
        </div>
      )}
      {relabel && <PackingRelabel order={order} />}
      {courier && <CourierSend order={order} view={courier} />}
    </ResultCard>
  );
};
